
import React, { useState } from 'react';
import MainLayout from '@/components/layouts/MainLayout';
import PageHeader from '@/components/common/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, Plus, Edit, Trash2 } from 'lucide-react';
import { useBooks, useDeleteBook } from '@/hooks/useBooks';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDate } from '@/utils/formatters';
import { Badge } from '@/components/ui/badge';
import AddBookDialog from '@/components/admin/AddBookDialog';
import EditBookDialog from '@/components/admin/EditBookDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

const AdminBooks: React.FC = () => {
  const { data: books, isLoading } = useBooks();
  const deleteBook = useDeleteBook();
  const [searchQuery, setSearchQuery] = useState('');

  // Group books by title and combine formats
  const groupedBooks = React.useMemo(() => {
    if (!books) return [];

    const bookMap = new Map();
    
    books.forEach(book => {
      const key = book.title.toLowerCase();
      if (bookMap.has(key)) {
        const existing = bookMap.get(key);
        existing.formats.push({
          id: book.id,
          format: book.format || 'paperback',
          isbn: book.isbn,
          publishDate: book.publishDate,
          book: book
        });
      } else {
        bookMap.set(key, {
          id: book.id,
          title: book.title,
          author: book.author,
          formats: [{
            id: book.id,
            format: book.format || 'paperback',
            isbn: book.isbn,
            publishDate: book.publishDate,
            book: book
          }]
        });
      }
    });

    return Array.from(bookMap.values());
  }, [books]);

  // Filter books based on search query
  const filteredBooks = React.useMemo(() => {
    if (!searchQuery) return groupedBooks;
    
    const query = searchQuery.toLowerCase();
    
    return groupedBooks.filter(book => 
      book.title.toLowerCase().includes(query) || 
      book.formats.some(format => format.isbn.toLowerCase().includes(query)) ||
      book.author?.name?.toLowerCase().includes(query)
    );
  }, [groupedBooks, searchQuery]);

  const handleDeleteBook = async (bookId: string) => {
    try {
      await deleteBook.mutateAsync(bookId);
    } catch (error) {
      console.error('Error deleting book:', error);
    }
  };

  // Function to truncate title
  const truncateTitle = (title: string, maxLength: number = 40) => {
    if (title.length <= maxLength) return title;
    return title.substring(0, maxLength) + '...';
  };
  
  return (
    <MainLayout allowedRoles={['admin']}>
      <PageHeader
        title="Books Management"
        description="Manage all books and assign them to authors"
      />
      
      <div className="mb-6">
        <Card className="bg-card dark:bg-card border-border dark:border-border">
          <CardHeader className="bg-card dark:bg-card">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <CardTitle className="text-card-foreground dark:text-card-foreground">Books Directory</CardTitle>
              
              <div className="flex w-full md:w-auto items-center gap-2">
                <div className="relative w-full md:w-80">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search books..."
                    className="pl-8 bg-background dark:bg-background border-input dark:border-input text-foreground dark:text-foreground"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                
                <AddBookDialog />
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="bg-card dark:bg-card">
            {isLoading ? (
              <div className="flex justify-center p-8">
                <p className="text-muted-foreground">Loading books...</p>
              </div>
            ) : filteredBooks.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="border-border dark:border-border">
                    <TableHead className="w-[250px] text-muted-foreground">Title</TableHead>
                    <TableHead className="text-muted-foreground">Author</TableHead>
                    <TableHead className="text-muted-foreground">Formats & ISBNs</TableHead>
                    <TableHead className="text-muted-foreground">Publication Dates</TableHead>
                    <TableHead className="text-muted-foreground w-24">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredBooks.map(book => (
                    <TableRow key={book.id} className="border-border dark:border-border hover:bg-muted/50 dark:hover:bg-muted/50">
                      <TableCell className="font-medium text-foreground dark:text-foreground" title={book.title}>
                        {truncateTitle(book.title)}
                      </TableCell>
                      <TableCell className="text-foreground dark:text-foreground">{book.author?.name || 'Unknown'}</TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {book.formats.map((format, index) => (
                            <div key={index} className="flex items-center gap-2">
                              <Badge variant="outline" className="capitalize border-border dark:border-border text-foreground dark:text-foreground">
                                {format.format}
                              </Badge>
                              <span className="text-sm text-muted-foreground">
                                {format.isbn}
                              </span>
                            </div>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {book.formats.map((format, index) => (
                            <div key={index} className="text-sm text-foreground dark:text-foreground">
                              {formatDate(format.publishDate)}
                            </div>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          {book.formats.map((format, index) => (
                            <div key={index} className="flex items-center gap-1">
                              <EditBookDialog book={format.book} />
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button variant="ghost" size="sm">
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete Book</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Are you sure you want to delete this book? This action cannot be undone.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => handleDeleteBook(format.id)}
                                      disabled={deleteBook.isPending}
                                    >
                                      {deleteBook.isPending ? 'Deleting...' : 'Delete'}
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          ))}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12">
                <p className="text-muted-foreground">No books found</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
};

export default AdminBooks;
